import React, { useEffect, useState } from "react";
import {
  Button,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Space,
  Spin,
  Tooltip,
  theme,
} from "antd";
import {
  CalendarOutlined,
  FileTextOutlined,
  DollarOutlined,
  CreditCardOutlined,
  SettingOutlined,
  FilePdfOutlined,
  PrinterOutlined,
  ReloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { AppDrawer } from "../../Common/Layout";
import CustomRangePicker, {
  RangeValue,
} from "../../Common/Custom Components/Ant design/RangePicker";
import { DailyPracticeRecord, PracticeMetricStats } from "./types";
import { formatCurrencyString } from "./practiceUtils";
import DailyPracticeStatsService from "../../Services/DailyPracticeStats";

const { Text } = Typography;

export interface PracticeQuickDrawerProps {
  open: boolean;
  onClose: () => void;
  record: DailyPracticeRecord | null;
  dateRangeText?: string;
}

interface StatTileProps {
  label: string;
  value: number | string;
  unit?: string;
}

const StatTile: React.FC<StatTileProps> = ({ label, value, unit }) => {
  const { token } = theme.useToken();
  const isZero =
    value === undefined ||
    value === null ||
    value === 0 ||
    value === "0" ||
    value === "0 min" ||
    value === "0 mins";

  const displayVal =
    typeof value === "number"
      ? isZero
        ? "0"
        : value.toLocaleString("en-GB")
      : value;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 10px",
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusSM,
        border: `1px solid ${token.colorBorderSecondary}`,
        gap: 6,
        minHeight: 32,
        boxSizing: "border-box",
      }}
    >
      <Text
        style={{
          fontSize: 11,
          color: token.colorTextSecondary,
          margin: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={label}
      >
        {label}
      </Text>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: isZero ? token.colorTextQuaternary : token.colorText,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {displayVal}
        {unit && !isZero ? ` ${unit}` : ""}
      </span>
    </div>
  );
};

interface FinancialRowProps {
  label: string;
  count: number;
  amount?: number;
  amountColor?: string;
}

const FinancialRow: React.FC<FinancialRowProps> = ({
  label,
  count,
  amount,
  amountColor = "blue",
}) => {
  const { token } = theme.useToken();
  const isZeroCount = !count || count === 0;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 12px",
        background: token.colorBgContainer,
        borderRadius: token.borderRadiusSM,
        border: `1px solid ${token.colorBorderSecondary}`,
        gap: 8,
        minHeight: 34,
        boxSizing: "border-box",
      }}
    >
      <Text
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: token.colorTextSecondary,
          margin: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={label}
      >
        {label}
      </Text>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: isZeroCount ? token.colorTextQuaternary : token.colorText,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {isZeroCount ? "0" : count.toLocaleString("en-GB")}
        </span>
        {amount !== undefined && (
          <Tag
            color={amount > 0 ? amountColor : "default"}
            bordered={false}
            style={{
              margin: 0,
              fontSize: 10,
              fontWeight: 500,
              fontVariantNumeric: "tabular-nums",
              padding: "0 4px",
              borderRadius: 3,
              lineHeight: "18px",
            }}
          >
            {formatCurrencyString(amount)}
          </Tag>
        )}
      </div>
    </div>
  );
};

export const PracticeQuickDrawer: React.FC<PracticeQuickDrawerProps> = ({
  open,
  onClose,
  record,
  dateRangeText,
}) => {
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("month"),
    dayjs().endOf("day"),
  ]);
  const [loadingMetrics, setLoadingMetrics] = useState<boolean>(false);
  const [downloadingPdf, setDownloadingPdf] = useState<boolean>(false);
  const [liveStats, setLiveStats] = useState<PracticeMetricStats | null>(null);

  // Initialize or reset stats when drawer opens or record changes
  useEffect(() => {
    if (open && record) {
      setLiveStats(record.stats || {});
      setDateRange([dayjs().startOf("month"), dayjs().endOf("day")]);
    }
  }, [open, record]);

  const activeDateRangeLabel = `${dateRange[0].format("DD/MM/YYYY")} – ${dateRange[1].format("DD/MM/YYYY")}`;

  const fetchMetricsForRange = async (range: [Dayjs, Dayjs]) => {
    if (!record?.practiceId) return;
    setLoadingMetrics(true);
    try {
      const fromStr = range[0].format("YYYY-MM-DD");
      const toStr = range[1].format("YYYY-MM-DD");
      const res: any = await DailyPracticeStatsService.getDailyPracticeStats({
        search: record.practiceName || "",
        fromDate: fromStr,
        toDate: toStr,
        length: 50,
      });

      if (res?.status && res?.result?.items) {
        const matching = res.result.items.find(
          (x: any) =>
            x.practiceId === record.practiceId ||
            (x.practiceName &&
              record.practiceName &&
              x.practiceName.toLowerCase() ===
                record.practiceName.toLowerCase()),
        );
        if (matching?.stats) {
          setLiveStats(matching.stats);
        } else {
          setLiveStats({});
        }
      }
    } catch {
      // Fallback
    } finally {
      setLoadingMetrics(false);
    }
  };

  const handleDateRangeChange = (dates: RangeValue) => {
    if (dates && dates[0] && dates[1]) {
      const newRange: [Dayjs, Dayjs] = [dates[0], dates[1]];
      setDateRange(newRange);
      fetchMetricsForRange(newRange);
    }
  };

  if (!record) return null;

  const stats: PracticeMetricStats = liveStats || record.stats || {};
  const totalFilings =
    (stats.ct || 0) +
    (stats.vat || 0) +
    (stats.cis || 0) +
    (stats.sA100 || 0) +
    (stats.sA800 || 0) +
    (stats.sA900 || 0) +
    (stats.cs || 0) +
    (stats.cf || 0) +
    (stats.fps || 0) +
    (stats.eps || 0) +
    (stats.accountsReport || 0);

  const totalCollected =
    (stats.receiptsAmount || 0) +
    (stats.stripeTotalAmount || 0) +
    (stats.goCardlessTotalAmount || 0);

  const handleNavigateToDetails = () => {
    navigate(`/practices/${record.practiceId}`, {
      state: { practiceName: record.practiceName },
    });
    onClose();
  };

  const handleDownloadPdf = async () => {
    if (!record?.practiceId) return;
    setDownloadingPdf(true);
    try {
      const fromStr = dateRange[0].format("YYYY-MM-DD");
      const toStr = dateRange[1].format("YYYY-MM-DD");
      await DailyPracticeStatsService.downloadPracticePdf(
        record.practiceId,
        fromStr,
        toStr,
      );
    } catch {
      // Error handled by ApiUtility
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const headerExtra = (
    <Space size={4} className="no-print">
      <Tooltip title="Download PDF report">
        <Button
          size="small"
          type="text"
          loading={downloadingPdf}
          icon={
            !downloadingPdf ? (
              <FilePdfOutlined style={{ fontSize: 16, color: "#ff4d4f" }} />
            ) : undefined
          }
          onClick={handleDownloadPdf}
        />
      </Tooltip>
      <Tooltip title="Print summary">
        <Button
          size="small"
          type="text"
          icon={<PrinterOutlined style={{ fontSize: 15 }} />}
          onClick={handlePrint}
        />
      </Tooltip>
    </Space>
  );

  return (
    <AppDrawer
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span>{record.practiceName || `Practice #${record.practiceId}`}</span>
          <Tag
            color="blue"
            bordered={false}
            style={{
              borderRadius: 4,
              margin: 0,
              fontWeight: 500,
              fontSize: 11,
            }}
          >
            Tenant #{record.practiceId}
          </Tag>
        </div>
      }
      open={open}
      onClose={onClose}
      width={680}
      showCancel={true}
      cancelText="Close"
      extra={headerExtra}
      extraFooter={
        <Button
          type="primary"
          icon={<SettingOutlined />}
          onClick={handleNavigateToDetails}
        >
          Manage Practice
        </Button>
      }
      bodyPadding="16px 20px"
      styles={{
        body: {
          backgroundColor: token.colorBgLayout,
        },
      }}
    >
      <style>{`
        @media print {
          body * {
            visibility: hidden !important;
          }
          .practice-quick-drawer-print, .practice-quick-drawer-print * {
            visibility: visible !important;
          }
          .practice-quick-drawer-print {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            padding: 20px !important;
            background: #ffffff !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <Spin
        spinning={loadingMetrics}
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      >
        <div
          className="practice-quick-drawer-print"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            padding: "12px 16px",
          }}
        >
          {/* Date Range Filter Bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 14px",
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <CalendarOutlined
                style={{ color: token.colorPrimary, fontSize: 14 }}
              />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: token.colorText,
                  whiteSpace: "nowrap",
                }}
              >
                Period
              </span>
              {loadingMetrics && (
                <Space size={6} className="no-print" style={{ marginLeft: 8 }}>
                  <ReloadOutlined
                    spin
                    style={{ color: token.colorPrimary, fontSize: 12 }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Updating...
                  </Text>
                </Space>
              )}
            </div>
            <div>
              <CustomRangePicker
                value={dateRange}
                allowClear={false}
                placement="bottomRight"
                onChange={handleDateRangeChange}
                style={{ width: 260 }}
                className="no-print"
              />
            </div>
          </div>

          {/* Executive KPI Summary Header */}
          <div
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
              padding: "10px 16px",
            }}
          >
            <Row gutter={[12, 12]}>
              <Col span={8}>
                <Statistic
                  title={
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: token.colorTextSecondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Total Filings
                    </span>
                  }
                  value={totalFilings}
                  valueStyle={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: token.colorPrimary,
                    lineHeight: 1.2,
                  }}
                  prefix={
                    <FileTextOutlined
                      style={{ fontSize: 14, marginRight: 4 }}
                    />
                  }
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title={
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: token.colorTextSecondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Invoices (Gross)
                    </span>
                  }
                  value={formatCurrencyString(stats.invoicesAmount)}
                  valueStyle={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: token.colorSuccess,
                    lineHeight: 1.2,
                  }}
                  prefix={
                    <DollarOutlined style={{ fontSize: 14, marginRight: 4 }} />
                  }
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title={
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: token.colorTextSecondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Collections
                    </span>
                  }
                  value={formatCurrencyString(totalCollected)}
                  valueStyle={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: token.colorInfo,
                    lineHeight: 1.2,
                  }}
                  prefix={
                    <CreditCardOutlined
                      style={{ fontSize: 14, marginRight: 4 }}
                    />
                  }
                />
              </Col>
            </Row>
          </div>

          {/* Section 1: Users Breakdown */}
          <div
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
              padding: "10px 14px",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: token.colorTextSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 8,
              }}
            >
              Users
            </div>
            <Row gutter={[8, 8]}>
              <Col xs={12} sm={6}>
                <StatTile label="Active" value={stats.activeUsers || 0} />
              </Col>
              <Col xs={12} sm={6}>
                <StatTile label="Inactive" value={stats.inactiveUsers || 0} />
              </Col>
              <Col xs={12} sm={6}>
                <StatTile
                  label="Newly Added"
                  value={stats.newlyAddedUsers || 0}
                />
              </Col>
              <Col xs={12} sm={6}>
                <StatTile label="Flushed" value={stats.flushedUsers || 0} />
              </Col>
            </Row>
          </div>

          {/* Section 2: Clients Breakdown */}
          <div
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
              padding: "10px 14px",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: token.colorTextSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 8,
              }}
            >
              Clients
            </div>
            <Row gutter={[8, 8]}>
              <Col xs={12} sm={6}>
                <StatTile label="Active" value={stats.activeClients || 0} />
              </Col>
              <Col xs={12} sm={6}>
                <StatTile label="Inactive" value={stats.inactiveClients || 0} />
              </Col>
              <Col xs={12} sm={6}>
                <StatTile
                  label="Newly Added"
                  value={stats.newlyAddedClients || 0}
                />
              </Col>
              <Col xs={12} sm={6}>
                <StatTile label="Flushed" value={stats.flushedClients || 0} />
              </Col>
            </Row>
          </div>

          {/* Section 3: Tax & Statutory Filings */}
          <div
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
              padding: "10px 14px",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: token.colorTextSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 8,
              }}
            >
              Tax & Statutory Filings
            </div>
            <Row gutter={[8, 8]}>
              <Col span={8}>
                <StatTile label="VAT Returns" value={stats.vat || 0} />
              </Col>
              <Col span={8}>
                <StatTile label="Corp. Tax (CT)" value={stats.ct || 0} />
              </Col>
              <Col span={8}>
                <StatTile label="Accounts" value={stats.accountsReport || 0} />
              </Col>
              <Col span={8}>
                <StatTile label="SA100" value={stats.sA100 || 0} />
              </Col>
              <Col span={8}>
                <StatTile
                  label="SA800 / SA900"
                  value={(stats.sA800 || 0) + (stats.sA900 || 0)}
                />
              </Col>
              <Col span={8}>
                <StatTile label="CIS" value={stats.cis || 0} />
              </Col>
              <Col span={8}>
                <StatTile label="FPS (Payroll)" value={stats.fps || 0} />
              </Col>
              <Col span={8}>
                <StatTile label="EPS (Payroll)" value={stats.eps || 0} />
              </Col>
              <Col span={8}>
                <StatTile
                  label="CS / CF"
                  value={(stats.cs || 0) + (stats.cf || 0)}
                />
              </Col>
            </Row>
          </div>

          {/* Section 4: Financial & Revenue Breakdown */}
          <div
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
              padding: "10px 14px",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: token.colorTextSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 8,
              }}
            >
              Financial & Revenue Breakdown
            </div>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <FinancialRow
                  label="Invoices Raised"
                  count={stats.invoicesCount || 0}
                  amount={stats.invoicesAmount || 0}
                  amountColor="green"
                />
              </Col>
              <Col span={12}>
                <FinancialRow
                  label="Receipts Logged"
                  count={stats.receiptsCount || 0}
                  amount={stats.receiptsAmount || 0}
                  amountColor="blue"
                />
              </Col>
              <Col span={12}>
                <FinancialRow
                  label="Stripe Volume"
                  count={stats.stripeCount || 0}
                  amount={stats.stripeTotalAmount || 0}
                  amountColor="purple"
                />
              </Col>
              <Col span={12}>
                <FinancialRow
                  label="GoCardless Volume"
                  count={stats.goCardlessCount || 0}
                  amount={stats.goCardlessTotalAmount || 0}
                  amountColor="cyan"
                />
              </Col>
              <Col span={24}>
                <FinancialRow
                  label="Total Collections"
                  count={
                    (stats.receiptsCount || 0) +
                    (stats.stripeCount || 0) +
                    (stats.goCardlessCount || 0)
                  }
                  amount={totalCollected}
                  amountColor="gold"
                />
              </Col>
            </Row>
          </div>

          {/* Section 5: Communications */}
          <div
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
              padding: "10px 14px",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: token.colorTextSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 8,
              }}
            >
              Communications
            </div>
            <Row gutter={[8, 8]}>
              <Col span={8}>
                <StatTile
                  label="No-reply Emails"
                  value={stats.noReplyEmails ?? stats.emails ?? 0}
                />
              </Col>
              <Col span={8}>
                <StatTile label="Incoming Calls" value={stats.incoming || 0} />
              </Col>
              <Col span={8}>
                <StatTile
                  label="Incoming Minutes"
                  value={stats.incomingMinutes || 0}
                  unit="mins"
                />
              </Col>
              <Col span={8}>
                <StatTile label="Outgoing Calls" value={stats.outgoing || 0} />
              </Col>
              <Col span={8}>
                <StatTile
                  label="Outgoing Minutes"
                  value={stats.outgoingMinutes || 0}
                  unit="mins"
                />
              </Col>
            </Row>
          </div>

          {/* Section 6: Operations */}
          <div
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadius,
              padding: "10px 14px",
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: token.colorTextSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 8,
              }}
            >
              Operations
            </div>
            <Row gutter={[8, 8]}>
              <Col span={8}>
                <StatTile label="Storage (MB)" value={stats.storageUses || 0} />
              </Col>
              <Col span={8}>
                <StatTile label="AML Checks" value={stats.aml || 0} />
              </Col>
              <Col span={8}>
                <StatTile
                  label="AML Biometric"
                  value={stats.amL_Biometric || 0}
                />
              </Col>
            </Row>
          </div>
        </div>
      </Spin>
    </AppDrawer>
  );
};

export default PracticeQuickDrawer;
